import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./BulkProductUploadModal.scss";
import { Form, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../../components/UI/Modal";
import { createCategoryList } from "../ComponentContainer/createCategoryList";
import { getProducts, getProductsBySlug } from "../../actions/product.action";
import { planBulkProductImport, uploadBulkProductItem } from "../../actions/product.action";
import { getAdminProductModelData } from "../../actions/officeAction";
import apiClient from "../../helpers/axios";
import {
  disposeCatalogModelData,
  measureCatalogModelGeometry,
} from "../CatalogReview/catalogGeometryMeasurement";

const DEFAULT_CATEGORY_ID = "681485ee43c2083aab468f40";
const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "webp"]);
const MODEL_EXTENSIONS = new Set(["glb", "gltf", "obj", "stl", "fbx"]);
const MODES = [
  { value: "create", text: "Создать новые товары" },
  { value: "updatePhotos", text: "Заменить только фотографии и все миниатюры" },
  { value: "updateModel", text: "Заменить только 3D-модель (GLB/STL/OBJ)" },
  { value: "updateAll", text: "Заменить фотографии и 3D-модель" },
  { value: "rebuildThumbs", text: "Восстановить миниатюры из существующих оригиналов" },
];

const collectGroups = (files) => {
  const map = new Map();
  for (const file of files) {
    const relative = String(file.webkitRelativePath || file.name).replace(/\\/g, "/");
    const parts = relative.split("/").filter(Boolean);
    if (parts.length < 2) continue;
    const folderPath = parts.slice(0, -1).join("/");
    const folderName = parts[parts.length - 2];
    if (!map.has(folderPath)) map.set(folderPath, {
      folderPath, folderName, files: [], images: [], models: [],
    });
    const entry = map.get(folderPath);
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    entry.files.push(file);
    if (IMAGE_EXTENSIONS.has(ext)) entry.images.push(file);
    if (MODEL_EXTENSIONS.has(ext)) entry.models.push(file);
  }
  return [...map.values()].sort((a, b) => a.folderPath.localeCompare(b.folderPath, "ru", {
    numeric: true, sensitivity: "base",
  }));
};
const getErrorText = (error) => error?.response?.data?.message || error?.message || "Неизвестная ошибка";
const needsPhotos = (mode) => mode === "create" || mode === "updatePhotos" || mode === "updateAll";
const needsModels = (mode) => mode === "create" || mode === "updateModel" || mode === "updateAll";
const formatSize = (size) => `${(size / 1024 / 1024).toFixed(1)} МБ`;
const formatDimensions = (dimensions = {}) => {
  const { height, width, thickness } = dimensions;
  return height > 0 && width > 0 && thickness > 0
    ? `${Math.round(height)}×${Math.round(width)}×${Math.round(thickness)} мм`
    : "—";
};

const BulkProductUploadModal = ({ show, handleClose, slug }) => {
  const dispatch = useDispatch();
  const folderInputRef = useRef(null);
  const runningRef = useRef(false);
  const categories = useSelector((state) => state.category.categories || []);
  const categoryList = useMemo(() => createCategoryList(categories), [categories]);

  const [files, setFiles] = useState([]);
  const [mode, setMode] = useState("create");
  const [form, setForm] = useState({
    categoryId: DEFAULT_CATEGORY_ID,
    visibleProduct: "false", visibilityPrice: "public", visibleWatermark: "visible",
    addWhiteBackground: "visible", quantity: "0", price: "",
  });
  const [plan, setPlan] = useState(null);
  const [checking, setChecking] = useState(false);
  const [running, setRunning] = useState(false);
  const [stage, setStage] = useState("");
  const [uploadPercent, setUploadPercent] = useState(0);
  const [current, setCurrent] = useState(0);
  const [total, setTotal] = useState(0);
  const [results, setResults] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [measurements, setMeasurements] = useState([]);
  const [measureModels, setMeasureModels] = useState(true);
  const [backfill, setBackfill] = useState({ loading: false, message: "", error: "" });

  const groups = useMemo(() => collectGroups(files), [files]);
  const busy = checking || running || backfill.loading;
  const photosCount = useMemo(() => groups.reduce((sum, group) => sum + group.images.length, 0), [groups]);
  const totalBytes = useMemo(() => files.reduce((sum, file) => sum + file.size, 0), [files]);
  const resetPlan = useCallback(() => {
    setPlan(null);
    setResults([]);
    setMeasurements([]);
    setErrorMessage("");
    setUploadPercent(0);
  }, []);

  useEffect(() => {
    if (show) return;
    // Do not reset the active queue just because a parent tried to close the modal.
    if (runningRef.current) return;
    setFiles([]);
    resetPlan();
    setStage("");
    setBackfill({ loading: false, message: "", error: "" });
    if (folderInputRef.current) folderInputRef.current.value = "";
  }, [show, resetPlan]);

  useEffect(() => {
    if (!busy) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [busy]);

  const changeForm = (name, value) => {
    setForm((previous) => ({ ...previous, [name]: value }));
    resetPlan();
  };
  const changeMode = (value) => {
    setMode(value);
    resetPlan();
  };
  const changeFiles = (event) => {
    if (busy) return;
    setFiles(Array.from(event.target.files || []));
    resetPlan();
  };
  const safeClose = () => {
    if (busy || runningRef.current) return;
    handleClose();
  };

  const verify = async () => {
    if (busy || runningRef.current) return;
    if (!form.categoryId || !groups.length) {
      setErrorMessage("Выберите категорию и папку с товарами");
      return;
    }
    setChecking(true);
    resetPlan();
    try {
      const response = await planBulkProductImport({
        categoryId: form.categoryId,
        folders: groups.map((group) => ({
          folderName: group.folderName,
          folderPath: group.folderPath,
          model: group.models[0]?.name || null,
          imagesCount: group.images.length,
        })),
      });
      const rows = response?.rows || [];
      const byPath = new Map(rows.map((row) => [row.folderPath, row]));
      setPlan(groups.map((group) => ({
        ...group,
        ...(byPath.get(group.folderPath) || { state: "conflict", message: "Папка не проверена" }),
      })));
    } catch (error) {
      setErrorMessage(getErrorText(error));
    } finally {
      setChecking(false);
    }
  };

  const canUpload = (group) => {
    if (group.state === "conflict") return false;
    if (mode === "create" && group.state !== "new") return false;
    if (mode !== "create" && group.state !== "existing") return false;
    if (needsPhotos(mode) && !group.images.length) return false;
    if (needsModels(mode) && group.models.length !== 1) return false;
    return true;
  };
  const jobs = (plan || []).filter(canUpload);

  const refreshCatalog = () => {
    let settings = {};
    try { settings = JSON.parse(localStorage.getItem("settings") || "{}") || {}; } catch (_) { /* ignore */ }
    const action = slug && slug !== "all"
      ? getProductsBySlug(slug, settings.sort, settings.pageNumber, settings.pageSize)
      : getProducts(settings.sort, settings.pageNumber, settings.pageSize);
    dispatch(action);
  };

  const measureCreatedModels = async (items) => {
    const rows = [];
    for (let index = 0; index < items.length; index += 1) {
      const item = items[index];
      let modelData = null;
      setStage(`Измерение 3D: ${index + 1}/${items.length} — ${item.fullName || item.productId}`);
      try {
        const action = await dispatch(getAdminProductModelData({
          _id: item.productId,
          fullName: item.fullName || "",
        }, { cache: false }));
        modelData = action?.payload || null;
        const measurement = measureCatalogModelGeometry({ modelData, productDimensions: {} });
        const response = await apiClient.post(
          `/admin/product/model/${encodeURIComponent(item.productId)}/measurement`,
          { measurement, source: "bulk-upload-3d", initializeBaseDimensions: true }
        );
        rows.push({ productId: item.productId, name: item.fullName,
          ok: true, dimensions: response?.data?.measurement?.dimensions || measurement.dimensions });
      } catch (error) {
        rows.push({ productId: item.productId, name: item.fullName,
          ok: false, message: getErrorText(error) });
      } finally {
        if (modelData) disposeCatalogModelData(modelData);
        setMeasurements([...rows]);
      }
    }
  };

  const runQueue = async () => {
    if (busy || runningRef.current) return;
    if (!plan) {
      setErrorMessage("Сначала нажмите «Проверить» (файлы на сервер не отправляются)");
      return;
    }
    if (!jobs.length) {
      setErrorMessage("Нет подходящих товаров. Проверьте режим импорта и совпадения папок.");
      return;
    }
    runningRef.current = true;
    setRunning(true);
    setResults([]);
    setMeasurements([]);
    setErrorMessage("");
    setTotal(jobs.length);
    setCurrent(0);
    const completed = [];
    const modelsToMeasure = [];
    let uncertain = false;
    try {
      for (let index = 0; index < jobs.length; index += 1) {
        const group = jobs[index];
        setCurrent(index + 1);
        setUploadPercent(0);
        setStage(`Загрузка товара ${index + 1}/${jobs.length}: ${group.folderName}`);
        try {
          // Send only the files required by the chosen mode.
          const selected = [
            ...(needsPhotos(mode) ? group.images : []),
            ...(needsModels(mode) ? group.models : []),
          ];
          const formData = new FormData();
          Object.entries(form).forEach(([key, value]) => formData.append(key, value));
          formData.append("mode", mode);
          formData.append("folderName", group.folderName);
          if (group.productId) formData.append("productId", group.productId);
          selected.forEach((file) => formData.append("bulkFiles", file));
          const data = await uploadBulkProductItem(formData, ({ loaded, total: bytesTotal }) => {
            if (bytesTotal) {
              const percent = Math.min(100, Math.round(100 * loaded / bytesTotal));
              setUploadPercent(percent);
              if (percent === 100) setStage(`Обработка товара на сервере: ${group.folderName}`);
            }
          });
          const item = data?.item || {};
          const result = { folderName: group.folderName, status: item.status || "unknown",
            fullName: item.fullName, message: item.message, productId: item.productId };
          completed.push(result);
          setResults([...completed]);
          if (item.modelChanged && item.productId && measureModels && item.status !== "skipped") {
            modelsToMeasure.push(item);
          }
        } catch (error) {
          const hasNoResponse = !error?.response;
          completed.push({ folderName: group.folderName, status: "error",
            message: `${getErrorText(error)}${hasNoResponse ? " — результат на сервере неизвестен" : ""}` });
          setResults([...completed]);
          if (hasNoResponse) {
            uncertain = true;
            setErrorMessage("Связь оборвалась. Не запускайте повтор без новой проверки — товар мог сохраниться.");
            break;
          }
        }
      }
      if (!uncertain && modelsToMeasure.length) await measureCreatedModels(modelsToMeasure);
      setPlan(null); // Require a fresh server check before any retry or repeat.
      refreshCatalog();
      setStage(uncertain ? "Остановлено после потери соединения" : "Завершено");
    } finally {
      runningRef.current = false;
      setRunning(false);
      setUploadPercent(0);
    }
  };

  const backfillMeasurements = async () => {
    if (busy || runningRef.current) return;
    setBackfill({ loading: true, message: "", error: "" });
    try {
      const response = await apiClient.post("/admin/product/model-measurements/backfill");
      setBackfill({ loading: false,
        message: response?.data?.message || "Размеры синхронизированы", error: "" });
    } catch (error) {
      setBackfill({ loading: false, message: "", error: getErrorText(error) });
    }
  };

  const successfulCount = results.filter((item) => ["created", "updated"].includes(item.status)).length;
  const failedCount = results.filter((item) => item.status === "error").length;
  const skippedCount = results.filter((item) => item.status === "skipped").length;
  const readyCount = jobs.length;
  const isFinished = !busy && results.length > 0 && !errorMessage;

  return (
    <Modal
      size="xl"
      show={show}
      handleClose={safeClose}
      modalTitle="Импорт и обновление товаров"
      titleId="bulk-import-dialog-title"
      className="bulkImportModal"
      dialogClassName="bulkImportModal__dialog"
      contentClassName="bulkImportModal__content"
      bodyClassName="bulkImportModal__body"
      closeButton={!busy}
      preventOutsideClose={busy}
      footer={(
        <div className="bulkImportModal__footerInner">
          <div className="bulkImportModal__footerNote" aria-live="polite">
            <span className={`bulkImportModal__footerDot${busy ? " is-busy" : ""}`} />
            {running
              ? `Загрузка ${current} из ${total}. Не закрывайте вкладку.`
              : checking
                ? "Проверяем товары в каталоге"
                : backfill.loading
                  ? "Синхронизация размеров"
                  : isFinished
                    ? `Готово: ${successfulCount} успешно`
                    : plan
                      ? `Готово к отправке: ${readyCount}`
                      : "Начните с предварительной проверки"}
          </div>
          <div className="bulkImportModal__footerActions">
            <Button
              type="button"
              className="bulkImportModal__button bulkImportModal__button--ghost"
              variant="outline-secondary"
              disabled={busy}
              onClick={safeClose}
            >
              Закрыть
            </Button>
            <Button
              type="button"
              className="bulkImportModal__button bulkImportModal__button--secondary"
              variant="outline-primary"
              disabled={busy || !groups.length}
              onClick={verify}
            >
              {checking ? "Проверяем…" : "Проверить"}
            </Button>
            <Button
              type="button"
              className="bulkImportModal__button bulkImportModal__button--primary"
              variant="primary"
              disabled={busy || !plan || !jobs.length}
              onClick={runQueue}
            >
              {running ? `Обрабатываем ${current}/${total}` : `Начать загрузку${readyCount ? ` · ${readyCount}` : ""}`}
            </Button>
          </div>
        </div>
      )}
    >
      <div className="bulkImportModal__intro">
        <div className="bulkImportModal__introCopy">
          <span className="bulkImportModal__eyebrow">Каталог / Пакетная обработка</span>
          <p>Создавайте товары и заменяйте фотографии или 3D-модели без потери артикулов, цен и настроек.</p>
        </div>
        <div className="bulkImportModal__introBadge">1 товар = 1 запрос</div>
      </div>

      <div className="bulkImportModal__workspace">
        <section className="bulkImportModal__section" aria-labelledby="bulk-import-settings-title">
          <div className="bulkImportModal__sectionHeader">
            <span className="bulkImportModal__step">01</span>
            <div>
              <h3 id="bulk-import-settings-title">Параметры импорта</h3>
              <p>Выберите режим работы и категорию товаров.</p>
            </div>
          </div>
          <div className="bulkImportModal__fields">
            <Form.Group className="bulkImportModal__field bulkImportModal__field--wide">
              <Form.Label>Режим</Form.Label>
              <Form.Control as="select" value={mode} disabled={busy}
                onChange={(event) => changeMode(event.target.value)}>
                {MODES.map((item) => <option key={item.value} value={item.value}>{item.text}</option>)}
              </Form.Control>
              <span className="bulkImportModal__hint">
                {mode === "create"
                  ? "Создадим только отсутствующие товары. Существующие останутся без изменений."
                  : mode === "updatePhotos"
                    ? "Заменим оригиналы и миниатюры; текущая 3D-модель останется без изменений."
                    : mode === "updateModel"
                      ? "Заменим только модель и обновим её замер; фотографии сохраним."
                      : mode === "updateAll"
                        ? "Заменим фотографии и модель, сохранив карточку товара и её настройки."
                        : "Пересоздадим миниатюры из уже загруженных оригиналов без замены фото."}
              </span>
            </Form.Group>
            <Form.Group className="bulkImportModal__field bulkImportModal__field--category">
              <Form.Label>Категория</Form.Label>
              <Form.Control as="select" value={form.categoryId} disabled={busy}
                onChange={(event) => changeForm("categoryId", event.target.value)}>
                <option value="">Выберите категорию</option>
                {categoryList.map((item) => <option key={item.value} value={item.value}>{item.name}</option>)}
              </Form.Control>
            </Form.Group>
            {mode === "create" && (
              <>
                <Form.Group className="bulkImportModal__field">
                  <Form.Label>Количество</Form.Label>
                  <Form.Control type="number" min="0" value={form.quantity} disabled={busy}
                    onChange={(event) => changeForm("quantity", event.target.value)} />
                </Form.Group>
                <Form.Group className="bulkImportModal__field">
                  <Form.Label>Видимость товара</Form.Label>
                  <Form.Control as="select" value={form.visibleProduct} disabled={busy}
                    onChange={(event) => changeForm("visibleProduct", event.target.value)}>
                    <option value="false">Только админ</option>
                    <option value="true">Публичный</option>
                  </Form.Control>
                </Form.Group>
                <Form.Group className="bulkImportModal__field">
                  <Form.Label>Видимость цены</Form.Label>
                  <Form.Control as="select" value={form.visibilityPrice} disabled={busy}
                    onChange={(event) => changeForm("visibilityPrice", event.target.value)}>
                    <option value="private">Никому</option>
                    <option value="public">Публичная</option>
                    <option value="admin">Только админ</option>
                  </Form.Control>
                </Form.Group>
                <Form.Group className="bulkImportModal__field">
                  <Form.Label>Цена</Form.Label>
                  <Form.Control type="number" min="0" value={form.price} disabled={busy} placeholder="Не задана"
                    onChange={(event) => changeForm("price", event.target.value)} />
                </Form.Group>
              </>
            )}
            {needsPhotos(mode) && (
              <>
                <Form.Group className="bulkImportModal__field">
                  <Form.Label>Водяной знак</Form.Label>
                  <Form.Control as="select" value={form.visibleWatermark} disabled={busy}
                    onChange={(event) => changeForm("visibleWatermark", event.target.value)}>
                    <option value="visible">Наложить</option>
                    <option value="hide">Без водяного знака</option>
                  </Form.Control>
                </Form.Group>
                <Form.Group className="bulkImportModal__field">
                  <Form.Label>Белый фон</Form.Label>
                  <Form.Control as="select" value={form.addWhiteBackground} disabled={busy}
                    onChange={(event) => changeForm("addWhiteBackground", event.target.value)}>
                    <option value="visible">Добавить</option>
                    <option value="hide">Не добавлять</option>
                  </Form.Control>
                </Form.Group>
              </>
            )}
          </div>
        </section>

        <section className="bulkImportModal__section" aria-labelledby="bulk-import-source-title">
          <div className="bulkImportModal__sectionHeader">
            <span className="bulkImportModal__step">02</span>
            <div>
              <h3 id="bulk-import-source-title">Исходные файлы</h3>
              <p>Укажите общую папку с подготовленными товарами.</p>
            </div>
          </div>
          <label className={`bulkImportModal__dropzone${busy ? " is-disabled" : ""}`}>
            <input ref={folderInputRef} className="bulkImportModal__fileInput" type="file" multiple
              webkitdirectory="true" directory="true" disabled={busy} onChange={changeFiles} />
            <span className="bulkImportModal__folderIcon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"/>
                <path d="M3 10h18"/>
              </svg>
            </span>
            <span className="bulkImportModal__dropzoneCopy">
              <strong>{groups.length ? "Выбрать другую папку" : "Выбрать папку с товарами"}</strong>
              <small>{groups.length ? "Выбраны файлы для импорта" : "Внутри каждой папки — фотографии и файл модели"}</small>
            </span>
            <span className="bulkImportModal__dropzoneAction">Обзор</span>
          </label>
          <div className="bulkImportModal__stats" aria-label="Выбранные файлы">
            <div><strong>{groups.length}</strong><span>папок</span></div>
            <div><strong>{photosCount}</strong><span>фото</span></div>
            <div><strong>{files.length}</strong><span>файлов</span></div>
            <div><strong>{formatSize(totalBytes)}</strong><span>общий размер</span></div>
          </div>
          <div className="bulkImportModal__sourceFooter">
            {needsModels(mode) ? (
              <Form.Check type="checkbox" checked={measureModels} disabled={busy}
                onChange={(event) => setMeasureModels(event.target.checked)}
                label="Измерять 3D-модели после загрузки" />
            ) : <span className="bulkImportModal__hint">3D-модели при этом режиме не изменяются.</span>}
            <Button type="button" variant="link" className="bulkImportModal__link" disabled={busy || !files.length}
              onClick={() => { setFiles([]); resetPlan(); if (folderInputRef.current) folderInputRef.current.value = ""; }}>
              Очистить выбор
            </Button>
          </div>
        </section>
      </div>

      {(busy || errorMessage || stage || results.length > 0) && (
        <section className="bulkImportModal__progress" aria-live="polite" aria-label="Состояние импорта">
          <div className="bulkImportModal__progressHeader">
            <span className={`bulkImportModal__progressIndicator${busy ? " is-busy" : ""}`} aria-hidden="true" />
            <div className="bulkImportModal__progressCopy">
              <strong>{running ? "Импорт выполняется" : checking ? "Проверяем каталог" : backfill.loading
                ? "Синхронизация размеров" : errorMessage ? "Требуется внимание" : "Последняя операция"}</strong>
              <span>{stage || (checking ? "Проверка доступности товаров…" : backfill.loading ? "Обрабатываем размеры…"
                : busy ? "Ожидаем ответ сервера…" : "Операция завершена")}</span>
            </div>
            {running && <span className="bulkImportModal__progressPercent">{uploadPercent}%</span>}
            {busy && <span className="bulkImportModal__lock">Окно заблокировано</span>}
          </div>
          {running && (
            <div className="bulkImportModal__progressTrack" role="progressbar" aria-label="Передача текущего товара"
              aria-valuemin={0} aria-valuemax={100} aria-valuenow={uploadPercent}>
              <span style={{ width: `${uploadPercent}%` }} />
            </div>
          )}
          {errorMessage && <div className="bulkImportModal__error" role="alert">{errorMessage}</div>}
        </section>
      )}

      {plan && (
        <section className="bulkImportModal__section bulkImportModal__section--table" aria-labelledby="bulk-import-plan-title">
          <div className="bulkImportModal__sectionHeader bulkImportModal__sectionHeader--split">
            <div className="bulkImportModal__sectionHeaderTitle">
              <span className="bulkImportModal__step">03</span>
              <div>
                <h3 id="bulk-import-plan-title">Предварительная проверка</h3>
                <p>Сопоставление папок с товарами каталога.</p>
              </div>
            </div>
            <span className="bulkImportModal__counter">К обработке: {jobs.length} из {plan.length}</span>
          </div>
          <div className="bulkImportModal__tableHeader" aria-hidden="true">
            <span>Исходная папка</span><span>Состояние</span><span>Состав</span>
          </div>
          <div className="bulkImportModal__table" role="list">
            {plan.map((group) => {
              const selected = canUpload(group);
              const status = group.state === "conflict" ? "Конфликт"
                : group.state === "existing" ? "Существует" : "Новый";
              const warning = needsPhotos(mode) && !group.images.length ? "Нет фотографий"
                : needsModels(mode) && group.models.length !== 1 ? "Нужна одна модель" : "";
              const tone = group.state === "conflict" ? "danger" : selected ? "success" : "muted";
              return (
                <div className="bulkImportModal__tableRow" role="listitem" key={group.folderPath}>
                  <div className="bulkImportModal__rowName">
                    <strong title={group.folderPath}>{group.folderName}</strong>
                    {group.fullName && <small>{group.fullName}</small>}
                    {(group.message || warning) && <small className={warning ? "is-warning" : ""}>{warning || group.message}</small>}
                  </div>
                  <div className="bulkImportModal__rowStatus">
                    <span className={`bulkImportModal__pill bulkImportModal__pill--${tone}`}>{selected ? "В очереди" : "Пропуск"}</span>
                    <small>{status}</small>
                  </div>
                  <div className="bulkImportModal__rowFiles">
                    <span>Фото <b>{group.images.length}</b></span>
                    <span>3D <b>{group.models.length}</b></span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {results.length > 0 && (
        <section className="bulkImportModal__section bulkImportModal__section--table" aria-labelledby="bulk-import-result-title">
          <div className="bulkImportModal__sectionHeader bulkImportModal__sectionHeader--split">
            <div className="bulkImportModal__sectionHeaderTitle">
              <span className="bulkImportModal__step">04</span>
              <div><h3 id="bulk-import-result-title">Результаты</h3><p>Итоги загрузки по каждому товару.</p></div>
            </div>
            <div className="bulkImportModal__resultSummary">
              <span className="is-success">{successfulCount} успешно</span>
              <span className="is-danger">{failedCount} ошибок</span>
              <span>{skippedCount} пропущено</span>
            </div>
          </div>
          <div className="bulkImportModal__table bulkImportModal__table--results" role="list">
            {results.map((item, index) => {
              const success = item.status === "created" || item.status === "updated";
              const skipped = item.status === "skipped";
              return (
                <div className="bulkImportModal__resultRow" role="listitem" key={`${item.folderName}-${index}`}>
                  <span className={`bulkImportModal__resultIcon${success ? " is-success" : skipped ? " is-muted" : " is-danger"}`}
                    aria-hidden="true">{success ? "✓" : skipped ? "−" : "!"}</span>
                  <div className="bulkImportModal__rowName">
                    <strong>{item.folderName}</strong>
                    {item.fullName && <small>{item.fullName}</small>}
                    {item.message && <small>{item.message}</small>}
                  </div>
                  <span className={`bulkImportModal__pill bulkImportModal__pill--${success ? "success" : skipped ? "muted" : "danger"}`}>
                    {item.status === "created" ? "Создан" : item.status === "updated" ? "Обновлён"
                      : skipped ? "Пропущен" : "Ошибка"}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {measurements.length > 0 && (
        <details className="bulkImportModal__details">
          <summary>Замеры 3D-моделей <span>{measurements.filter((item) => item.ok).length} из {measurements.length} успешно</span></summary>
          <div className="bulkImportModal__measurementRows">
            {measurements.map((item) => (
              <div key={item.productId}>
                <strong>{item.name}</strong>
                <span className={item.ok ? "is-success" : "is-danger"}>
                  {item.ok ? formatDimensions(item.dimensions) : `Ошибка: ${item.message}`}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}

      <details className="bulkImportModal__details bulkImportModal__details--tools">
        <summary>Дополнительные действия <span>Синхронизация размеров 3D</span></summary>
        <div className="bulkImportModal__tools">
          <p>Подтянуть уже сохранённые результаты измерений моделей из каталога.</p>
          <Button type="button" variant="outline-primary" className="bulkImportModal__button bulkImportModal__button--secondary"
            disabled={busy} onClick={backfillMeasurements}>
            {backfill.loading ? "Синхронизация…" : "Подтянуть измеренные 3D-размеры"}
          </Button>
          {backfill.message && <p className="bulkImportModal__toolSuccess" role="status">{backfill.message}</p>}
          {backfill.error && <p className="bulkImportModal__error" role="alert">{backfill.error}</p>}
        </div>
      </details>
    </Modal>
  );
};

export default BulkProductUploadModal;
